import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const OddParityBitCalculator = () => {
  const [inputData, setInputData] = useState({
    binaryNumber: '',
    parityBit: '',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateParity = () => {
    const { binaryNumber } = inputData;
    
    // Validate input
    if (!binaryNumber) {
      setError('Please enter a binary number');
      return;
    }

    // Remove any spaces and validate binary format
    const cleanBinary = binaryNumber.replace(/\s/g, '');
    if (!/^[01]+$/.test(cleanBinary)) {
      setError('Please enter a valid binary number (only 0s and 1s)');
      return;
    }

    // Calculate odd parity
    const onesCount = cleanBinary.split('').filter(bit => bit === '1').length;
    const parityBit = onesCount % 2 === 0 ? '1' : '0';
    const result = cleanBinary + parityBit;

    // Create visualization data
    const bitValues = [...cleanBinary, parityBit].map(bit => parseInt(bit));
    const bitLabels = [...cleanBinary.split(''), 'P'].map((_, i) => `Bit ${i}`);

    const chartData = {
      labels: bitLabels,
      datasets: [{
        label: 'Bit Value',
        data: bitValues,
        backgroundColor: bitValues.map(v => v === 1 ? 'rgba(255, 99, 132, 0.5)' : 'rgba(54, 162, 235, 0.5)'),
        borderColor: bitValues.map(v => v === 1 ? 'rgba(255, 99, 132, 1)' : 'rgba(54, 162, 235, 1)'),
        borderWidth: 1
      }]
    };

    const calculationSteps = [
      'Step 1: Count the number of 1s in the binary number',
      `Number of 1s: ${onesCount}`,
      '\nStep 2: Determine the parity bit',
      onesCount % 2 === 0 
        ? 'Even number of 1s, so parity bit is 1'
        : 'Odd number of 1s, so parity bit is 0',
      '\nStep 3: Append the parity bit to the original number',
      `Original: ${cleanBinary}`,
      `Parity Bit: ${parityBit}`,
      `Result: ${result}`
    ];

    setResults({
      originalNumber: cleanBinary,
      parityBit,
      result,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    binaryNumber: '101101',
    parityBit: '1',
    result: '1011011'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Odd Parity Bit Calculator | Binary Parity Check Tool</title>
          <meta name="description" content="Calculate odd parity bits for binary numbers. This calculator helps ensure error detection in data transmission by maintaining an odd number of 1s in the binary sequence." />
          <meta name="keywords" content="parity bit calculator, odd parity, binary calculator, error detection, computer science" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">💻</div>
            <h1 className="text-3xl font-bold text-gray-800">Odd Parity Bit Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the odd parity bit for a binary number. This calculator helps ensure error detection in data transmission by maintaining an odd number of 1s in the binary sequence.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Binary Number:</label>
              <input
                type="text"
                name="binaryNumber"
                value={inputData.binaryNumber}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter binary number (e.g., 101101)"
              />
            </div>
          </div>

          <button
            onClick={calculateParity}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Parity
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Parity Calculation</h3>
                  <p className="text-lg">Original Binary: {results.originalNumber}</p>
                  <p className="text-lg">Parity Bit: {results.parityBit}</p>
                  <p className="text-lg">Result: {results.result}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Bit Analysis</h3>
                  <p className="text-lg">Number of 1s: {results.originalNumber.split('').filter(bit => bit === '1').length}</p>
                  <p className="text-lg">Parity Type: Odd</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 1,
                          title: {
                            display: true,
                            text: 'Bit Value'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Bit Position'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Bit Values with Parity'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for odd parity:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Input Binary: {exampleData.binaryNumber}</li>
                <li>Calculated Parity Bit: {exampleData.parityBit}</li>
                <li>Result: {exampleData.result}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Count the number of 1s in the binary number
                  <ul className="list-disc pl-6">
                    <li>{exampleData.binaryNumber} has 4 ones</li>
                  </ul>
                </li>
                <li>Determine the parity bit
                  <ul className="list-disc pl-6">
                    <li>Even number of 1s → Parity bit = 1</li>
                    <li>Odd number of 1s → Parity bit = 0</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Odd Parity Bit Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Binary Number:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input your binary number (only 0s and 1s)</li>
                  <li>Spaces are automatically removed</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The number of 1s in your binary number</li>
                  <li>The required parity bit</li>
                  <li>The final result with parity bit</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated parity bit</li>
                  <li>Check the number of 1s</li>
                  <li>View the visual representation of bits</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Odd parity ensures an odd number of 1s</li>
                <li>Useful for error detection in data transmission</li>
                <li>Parity bit is always added at the end</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Parity Bits <span className="text-blue-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Parity bits are used in computer science and digital communications for error detection. They help ensure data integrity by:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Error Detection:</strong> Identifying single-bit errors in data transmission</li>
              <li><strong>Data Integrity:</strong> Maintaining consistent bit patterns</li>
              <li><strong>Network Communication:</strong> Ensuring reliable data transfer</li>
              <li><strong>Storage Systems:</strong> Protecting data integrity</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Double-check your binary input</li>
              <li><strong>Documentation:</strong> Record your calculations</li>
              <li><strong>Validation:</strong> Verify results when critical</li>
              <li><strong>Implementation:</strong> Follow system requirements</li>
            </ul>
            <p className="italic mt-4">
              Note: Always verify your calculations, especially when implementing parity checking in critical systems.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default OddParityBitCalculator;